<template>
  <div class="loading-container">
    <div class="loading-content">
      <ProgressSpinner
        :class="spinnerClass"
        strokeWidth="3"
        animationDuration="1s"
      />
      <span :class="textClass">{{ props.loadingType }}</span>
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed } from 'vue'
import ProgressSpinner from 'primevue/progressspinner'

const props = defineProps({
  loadingType: {
    type: String,
    default: 'Loading...'
  },
  size: {
    type: String,
    default: 'medium', // small, medium, large
    validator: (value) => ['small', 'medium', 'large'].includes(value)
  }
})

// Computed classes for responsive spinner sizing (optimized for Dialog)
const spinnerClass = computed(() => {
  const baseClasses = 'mb-3'

  switch (props.size) {
    case 'small':
      return `${baseClasses} w-6 h-6 sm:w-8 sm:h-8`
    case 'large':
      return `${baseClasses} w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14`
    default: // medium
      return `${baseClasses} w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12`
  }
})

// Computed classes for responsive text sizing (optimized for Dialog)
const textClass = computed(() => {
  const baseClasses = 'text-center font-medium text-gray-700 dark:text-gray-200 leading-relaxed max-w-full break-words'

  switch (props.size) {
    case 'small':
      return `${baseClasses} text-xs sm:text-sm`
    case 'large':
      return `${baseClasses} text-sm sm:text-base md:text-lg`
    default: // medium
      return `${baseClasses} text-xs sm:text-sm md:text-base`
  }
})
</script>

<style scoped>
.loading-container {
  @apply flex items-center justify-center w-full h-full p-4;

  /* Optimal height for Dialog usage */
  min-height: 120px;
}

.loading-content {
  @apply flex flex-col items-center justify-center text-center;

  /* Responsive padding for Dialog */
  @apply p-4 sm:p-6 md:p-8;

  /* Full width within Dialog */
  @apply w-full;
}

/* Custom spinner colors that work with PrimeVue themes */
:deep(.p-progress-spinner-circle) {
  @apply stroke-blue-500 dark:stroke-blue-400;
}

/* Smooth transitions for theme changes */
.loading-content {
  @apply transition-colors duration-200;
}

/* Responsive adjustments for very small screens */
@media (max-width: 320px) {
  .loading-content {
    @apply p-3;
  }

  .loading-container {
    min-height: 100px;
  }
}

/* Landscape orientation adjustments for mobile */
@media (max-height: 500px) and (orientation: landscape) {
  .loading-container {
    min-height: 80px;
  }

  .loading-content {
    @apply p-2;
  }
}

/* High DPI display adjustments */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  :deep(.p-progress-spinner-circle) {
    stroke-width: 2;
  }
}
</style>
